import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle, FileText, Award, Users } from 'lucide-react';

const Home = () => {
  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="gradient-bg pt-32 pb-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-5xl mx-auto text-center">
          <div className="animate-fadeInUp">
            <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold text-white mb-8">
              Bookkeeping That Actually Makes Sense
            </h1>
            <p className="text-xl md:text-2xl text-white/90 mb-10 max-w-4xl mx-auto leading-relaxed">
              Brown & Associates helps small businesses organize their books, rebuild accounting systems, and make confident decisions using their numbers.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link 
                to="/services#financial-diagnostic-review"
                className="btn-gold px-10 py-4 rounded-lg text-lg font-bold inline-flex items-center justify-center space-x-2"
              >
                <span>Start with a Diagnostic</span>
                <ArrowRight className="w-5 h-5" />
              </Link>
              <a 
                href="https://calendly.com/joede1123"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-white text-[#64B7B7] px-10 py-4 rounded-lg text-lg font-bold inline-flex items-center justify-center space-x-2 hover:bg-gray-100 transition"
              >
                <span>Schedule Consultation</span>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* As Featured In - 603 Diversity */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white" data-testid="featured-credibility-section">
        <div className="max-w-3xl mx-auto flex flex-col items-center text-center">
          <img
            src="https://customer-assets.emergentagent.com/job_cdb337a7-66c5-4432-af7b-14601414d200/artifacts/g0vkqgpk_603%20Diversity%20Magazine%20Cover%20Joede%20Brown%20April%202026.png"
            alt="Joede Brown on the cover of 603 Diversity Magazine, April 2026"
            className="w-full max-w-xs sm:max-w-sm md:max-w-md h-auto rounded-lg shadow-lg mb-6"
            data-testid="featured-magazine-cover"
          />
          <p className="text-base sm:text-lg md:text-xl text-gray-700 font-medium mb-6 max-w-xl" data-testid="featured-caption">
            As featured on the cover of 603 Diversity (April 2026)
          </p>
          <a
            href="https://calendly.com/joede1123"
            target="_blank"
            rel="noopener noreferrer"
            className="btn-gold px-8 py-3 rounded-lg font-bold inline-flex items-center justify-center"
            data-testid="featured-book-review-btn"
          >
            Book a Review
          </a>
        </div>
      </section>

      {/* What We Do */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4" style={{ color: '#64B7B7' }}>
              What We Do
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              We focus on making sure the numbers actually make sense
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-gray-50 p-8 rounded-xl text-center">
              <div className="w-16 h-16 mx-auto mb-6 rounded-full flex items-center justify-center" style={{ backgroundColor: '#B89A4E' }}>
                <FileText className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-bold mb-4" style={{ color: '#64B7B7' }}>
                Organize Books
              </h3>
              <p className="text-gray-600">
                Clean up past transactions and bring your bookkeeping current and accurate
              </p>
            </div>

            <div className="bg-gray-50 p-8 rounded-xl text-center">
              <div className="w-16 h-16 mx-auto mb-6 rounded-full flex items-center justify-center" style={{ backgroundColor: '#78C4C4' }}>
                <Award className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-bold mb-4" style={{ color: '#64B7B7' }}>
                Rebuild Systems
              </h3>
              <p className="text-gray-600">
                Create accounting structures that reflect what's really happening in your business
              </p>
            </div>

            <div className="bg-gray-50 p-8 rounded-xl text-center">
              <div className="w-16 h-16 mx-auto mb-6 rounded-full flex items-center justify-center" style={{ backgroundColor: '#64B7B7' }}>
                <Users className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-bold mb-4" style={{ color: '#64B7B7' }}>
                Provide Clarity
              </h3>
              <p className="text-gray-600">
                Review financial statements and help you understand what the numbers mean
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Start with Diagnostic */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-5xl mx-auto">
          <div className="bg-white rounded-2xl shadow-xl p-10 md:p-12">
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="text-3xl font-bold mb-6" style={{ color: '#64B7B7' }}>
                  Not Sure What You Need?
                </h2>
                <p className="text-xl text-gray-700 mb-6 leading-relaxed">
                  Start with a <span className="font-bold text-[#B89A4E]">diagnostic</span> to understand what's actually happening in your books.
                </p>
                <p className="text-lg text-gray-600 mb-8">
                  Most clients begin with a diagnostic before moving into cleanup or ongoing services. Diagnostic pricing is provided after reviewing the scope and condition of the books.
                </p>
                <Link 
                  to="/services#financial-diagnostic-review"
                  className="btn-gold px-8 py-3 rounded-lg font-bold inline-flex items-center space-x-2"
                >
                  <span>Start with a Diagnostic</span>
                  <ArrowRight className="w-5 h-5" />
                </Link>
              </div>

              <div className="space-y-4">
                <div className="flex items-start">
                  <CheckCircle className="w-6 h-6 mr-3 mt-1 flex-shrink-0" style={{ color: '#78C4C4' }} />
                  <span className="text-lg text-gray-700">Written diagnostic report (1–2 pages)</span>
                </div>
                <div className="flex items-start">
                  <CheckCircle className="w-6 h-6 mr-3 mt-1 flex-shrink-0" style={{ color: '#78C4C4' }} />
                  <span className="text-lg text-gray-700">Clear findings and recommendations</span>
                </div>
                <div className="flex items-start">
                  <CheckCircle className="w-6 h-6 mr-3 mt-1 flex-shrink-0" style={{ color: '#78C4C4' }} />
                  <span className="text-lg text-gray-700">Defined scope and pricing for next steps</span>
                </div>
                <div className="flex items-start">
                  <CheckCircle className="w-6 h-6 mr-3 mt-1 flex-shrink-0" style={{ color: '#78C4C4' }} />
                  <span className="text-lg text-gray-700">No obligation to proceed</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Who We Help */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4" style={{ color: '#64B7B7' }}>
              Who We Help
            </h2>
            <p className="text-xl text-gray-600">
              Business owners who need their numbers to make sense
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-gradient-to-br from-teal-50 to-white p-8 rounded-xl shadow-lg">
              <h3 className="text-xl font-bold mb-4" style={{ color: '#64B7B7' }}>
                When bookkeeping has fallen behind
              </h3>
              <p className="text-gray-600">
                Transactions have piled up and you need to get caught up before tax time or for business decisions
              </p>
            </div>

            <div className="bg-gradient-to-br from-teal-50 to-white p-8 rounded-xl shadow-lg">
              <h3 className="text-xl font-bold mb-4" style={{ color: '#64B7B7' }}>
                When reports don't match reality
              </h3>
              <p className="text-gray-600">
                Your financial statements don't reflect what's actually happening in your business
              </p>
            </div>

            <div className="bg-gradient-to-br from-teal-50 to-white p-8 rounded-xl shadow-lg">
              <h3 className="text-xl font-bold mb-4" style={{ color: '#64B7B7' }}>
                When tax season is approaching
              </h3>
              <p className="text-gray-600">
                You need accurate books for tax filing and you're not confident in what you have
              </p>
            </div>

            <div className="bg-gradient-to-br from-teal-50 to-white p-8 rounded-xl shadow-lg">
              <h3 className="text-xl font-bold mb-4" style={{ color: '#64B7B7' }}>
                When you need a professional review
              </h3>
              <p className="text-gray-600">
                You want an expert assessment of your bookkeeping before making decisions
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Experience Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-4xl mx-auto text-center">
          <div className="bg-white p-10 rounded-2xl shadow-lg">
            <div className="flex items-center justify-center mb-6">
              <div className="w-20 h-20 rounded-full flex items-center justify-center" style={{ backgroundColor: '#B89A4E' }}>
                <Award className="w-10 h-10 text-white" />
              </div>
            </div>
            <h2 className="text-3xl font-bold mb-4" style={{ color: '#64B7B7' }}>
              30+ Years of Experience
            </h2>
            <p className="text-xl text-gray-700 mb-6">
              Joede Brown has more than three decades of bookkeeping experience working with small businesses and recently completed her Master's degree in Accounting.
            </p>
            <Link 
              to="/about"
              className="text-[#B89A4E] font-semibold hover:underline inline-flex items-center"
            >
              <span>Learn more about Brown & Associates</span>
              <ArrowRight className="w-5 h-5 ml-2" />
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="gradient-bg-reverse py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <h3 className="text-3xl font-bold text-white mb-6">
            Ready to Make Sense of Your Numbers?
          </h3>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link 
              to="/services"
              className="bg-white text-[#64B7B7] px-8 py-3 rounded-lg font-bold inline-flex items-center justify-center hover:bg-gray-100 transition"
            >
              View Services
            </Link>
            <a 
              href="https://calendly.com/joede1123"
              target="_blank"
              rel="noopener noreferrer"
              className="btn-gold px-8 py-3 rounded-lg font-bold inline-block"
            >
              Schedule Consultation
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
